import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import useTransitionNavigation from '@/hooks/useTransitionNavigation';
import { motion, AnimatePresence } from 'framer-motion';
import { useSafeTranslation } from '@/hooks/useSafeTranslation';

import { useAuthStore, authHelpers } from '@/stores/authStore';
import { useRestaurantStore, restaurantHelpers } from '@/stores/restaurantStore';
import { Button } from '@/components/ui/Button';
import { PlanBadge } from '@/components/common/PartnershipBranding';
import { LogoFull, LogoIcon } from '@/assets/logo';
import {
  NavCard,
  NavChef,
  NavCoffee,
  NavDashboard,
  NavMonitor,
  NavOrders,
  NavPie,
  NavReceipt,
  NavSettings,
  NavShield,
  NavStaff,
  NavTables,
  NavTrending,
  NavUserCheck,
  NavUsers,
  NavLogout,
  NavClose,
} from '@/assets/nav-icons';
import { InitialsAvatar } from '@/components/common/InitialsAvatar';
import { cn } from '@/utils';
import type { UserRole } from '@/types';

// Navigation item interface for type safety
interface NavigationItem {
  id: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: UserRole[];
  permissions: string[];
  description: string;
  badge?: string;
  highlight?: boolean;
  restrictedRoles?: UserRole[];
  section?: string;
}

// Simplified role-based navigation configuration matching built features
export const getNavigationItems = (t: any): NavigationItem[] => [
  // DASHBOARD - Universal access
  {
    id: 'dashboard',
    label: t('nav.dashboard'),
    href: '/',
    icon: NavDashboard,
    roles: ['super_admin', 'restaurant_owner', 'manager', 'chef', 'waiter'],
    permissions: ['view_dashboard'],
    description: t('sidebar.descriptions.dashboardOverview'),
    section: 'main'
  },

  // SUPER ADMIN PLATFORM MANAGEMENT
  {
    id: 'platform-restaurants',
    label: t('superAdmin.restaurantPartners'),
    href: '/platform/restaurants',
    icon: NavCoffee,
    roles: ['super_admin'],
    permissions: ['platform_management'],
    description: t('sidebar.descriptions.platformRestaurants'),
    section: 'platform'
  },
  {
    id: 'platform-users',
    label: t('superAdmin.userManagement'),
    href: '/platform/users',
    icon: NavShield,
    roles: ['super_admin'],
    permissions: ['platform_management'],
    description: t('sidebar.descriptions.platformUsers'),
    section: 'platform'
  },
  {
    id: 'platform-analytics',
    label: t('superAdmin.platformAnalytics'),
    href: '/platform/analytics',
    icon: NavTrending,
    roles: ['super_admin'],
    permissions: ['platform_management'],
    description: t('sidebar.descriptions.platformAnalytics'),
    section: 'platform'
  },
  {
    id: 'platform-settings',
    label: t('superAdmin.platformSettings'),
    href: '/platform/settings',
    icon: NavSettings,
    roles: ['super_admin'],
    permissions: ['platform_management'],
    description: t('sidebar.descriptions.platformSettings'),
    section: 'platform'
  },

  // RESTAURANT OWNER SIDEBAR (Strategic Oversight Only)
  {
    id: 'admin-kitchen-monitor',
    label: t('nav.kitchenMonitor'),
    href: '/admin/kitchen-monitor',
    icon: NavMonitor,
    roles: ['restaurant_owner'],
    permissions: ['view_kitchen'],
    description: t('sidebar.descriptions.kitchenViewOnly'),
    section: 'owner'
  },
  {
    id: 'admin-staff-management',
    label: t('nav.employees'),
    href: '/employees',
    icon: NavUsers,
    roles: ['restaurant_owner'],
    permissions: ['manage_staff'],
    description: t('sidebar.descriptions.staffRolesPerformance'),
    section: 'owner'
  },
  {
    id: 'admin-analytics',
    label: t('nav.analytics'),
    href: '/admin/analytics',
    icon: NavTrending,
    roles: ['restaurant_owner'],
    permissions: ['view_analytics'],
    description: t('sidebar.descriptions.businessIntelligence'),
    section: 'owner'
  },
  {
    id: 'admin-settings',
    label: t('nav.settings'),
    href: '/admin/settings',
    icon: NavSettings,
    roles: ['restaurant_owner'],
    permissions: ['manage_settings'],
    description: t('sidebar.descriptions.systemConfig'),
    section: 'owner'
  },

  // MANAGER SIDEBAR (Operational Control)
  {
    id: 'manager-tables',
    label: t('nav.tables'),
    href: '/manager/tables',
    icon: NavTables,
    roles: ['restaurant_owner', 'manager'],
    permissions: ['manage_tables'],
    description: t('sidebar.descriptions.tableManagement'),
    section: 'manager'
  },
  {
    id: 'manager-orders',
    label: t('nav.orders'),
    href: '/manager/orders',
    icon: NavOrders,
    roles: ['restaurant_owner', 'manager'],
    permissions: ['view_orders', 'manage_orders'],
    description: t('sidebar.descriptions.orderOversight'),
    section: 'manager'
  },
  {
    // /menu is the working menu manager; /manager/menu is a "coming soon" stub
    // that this entry used to point at, which left the one screen that actually
    // edits the menu reachable only by typing its URL.
    id: 'manager-menu',
    label: t('nav.menu'),
    href: '/menu',
    icon: NavCoffee,
    roles: ['restaurant_owner', 'manager'],
    permissions: ['manage_menu'],
    description: t('sidebar.descriptions.menuPricing'),
    section: 'manager'
  },
  {
    // 'restaurant_owner' dropped: the owner's single Кухня entry is chef-kitchen below,
    // which is the only kitchen screen reading the live queue.
    id: 'manager-kitchen',
    label: t('nav.kitchen'),
    href: '/manager/kitchen',
    icon: NavChef,
    roles: ['manager'],
    permissions: ['view_kitchen', 'manage_kitchen_orders'],
    description: t('sidebar.descriptions.kitchenCoordination'),
    section: 'manager'
  },
  {
    // Staff has one canonical screen at /employees. This entry, the admin one
    // above and the waiter's profile link all rendered the same component under
    // three different routes; 'restaurant_owner' is deliberately absent here so the owner
    // sees one staff link, not three.
    id: 'manager-staff',
    label: t('nav.staff'),
    href: '/employees',
    icon: NavStaff,
    roles: ['manager'],
    permissions: ['manage_staff'],
    description: t('sidebar.descriptions.locationStaff'),
    section: 'manager'
  },
  {
    // 'restaurant_owner' dropped: duplicate of admin-analytics, same label, both placeholders.
    id: 'manager-analytics',
    label: t('nav.analytics'),
    href: '/manager/analytics',
    icon: NavPie,
    roles: ['manager'],
    permissions: ['view_analytics'],
    description: t('sidebar.descriptions.locationAnalytics'),
    section: 'manager'
  },

  // CHEF SIDEBAR (Kitchen Focus)
  {
    id: 'chef-kitchen',
    label: t('nav.kitchen'),
    href: '/chef/kitchen',
    icon: NavChef,
    roles: ['restaurant_owner', 'chef'],
    permissions: ['view_kitchen', 'manage_kitchen_orders'],
    description: t('sidebar.descriptions.kitchenDisplayManagement'),
    highlight: true,
    section: 'chef'
  },
  {
    // Same repoint as manager-menu. 'restaurant_owner' is dropped because the owner
    // already has a Меню entry above; /menu dispatches by role, so a chef still
    // lands on the chef's view.
    id: 'chef-menu',
    label: t('nav.menu'),
    href: '/menu',
    icon: NavReceipt,
    roles: ['chef'],
    permissions: ['view_menu'],
    description: t('sidebar.descriptions.readOnlyIngredients'),
    section: 'chef'
  },

  // WAITER SIDEBAR (Customer Service)
  {
    // 'manager' included: in a cafe the manager works the floor too, and
    // without the POS they cannot open a table's tab, take a second round or
    // settle a bill. The backend already allowed it — floorStaff covers
    // manager — so this route gate was the only thing in the way.
    id: 'waiter-pos',
    label: t('nav.pos'),
    href: '/waiter/pos',
    icon: NavCard,
    roles: ['restaurant_owner', 'manager', 'waiter'],
    permissions: ['place_orders'],
    description: t('sidebar.descriptions.orderCreation'),
    highlight: true,
    section: 'waiter'
  },
  {
    // 'restaurant_owner' dropped: an owner manages the floor at Столики; "my tables" is a
    // waiter's own assignment view.
    id: 'waiter-tables',
    label: t('nav.myTables'),
    href: '/waiter/tables',
    icon: NavTables,
    roles: ['waiter'],
    permissions: ['manage_tables'],
    description: t('sidebar.descriptions.assignedTables'),
    section: 'waiter'
  },
  {
    // 'restaurant_owner' dropped: duplicate of Заказы for the owner, and a placeholder.
    id: 'waiter-orders',
    label: t('nav.myOrders'),
    href: '/waiter/orders',
    icon: NavOrders,
    roles: ['waiter'],
    permissions: ['view_orders'],
    description: t('sidebar.descriptions.personalTracking'),
    section: 'waiter'
  },
  {
    // See manager-staff: same canonical screen, and 'restaurant_owner' is excluded so the
    // owner does not get a third link to it.
    id: 'waiter-profile',
    label: t('nav.profile'),
    href: '/employees',
    icon: NavUserCheck,
    roles: ['waiter'],
    permissions: ['view_dashboard'],
    description: t('sidebar.descriptions.personalPerformance'),
    section: 'waiter'
  },
];

// Role-specific section headers
const getSectionHeaders = (role: UserRole, t: any): Record<string, string> => {
  const headers: Record<UserRole, Record<string, string>> = {
    super_admin: {
      main: t('nav.dashboard'),
      platform: t('sidebar.sections.platformManagement'),
      owner: t('sidebar.sections.strategicOversight'),
      manager: t('sidebar.sections.operationalControl'),
      chef: t('sidebar.sections.kitchenFocus'),
      waiter: t('sidebar.sections.customerService')
    },
    restaurant_owner: {
      main: t('nav.dashboard'),
      owner: t('sidebar.sections.strategicOversight'),
      // The owner's day-to-day screens live in the manager and chef sections.
      // Without headers they rendered as a run of unlabelled links below
      // "Strategic oversight".
      manager: t('sidebar.sections.operationalControl'),
      chef: t('sidebar.sections.kitchenFocus')
    },
    manager: {
      main: t('nav.dashboard'),
      manager: t('sidebar.sections.operationalControl'),
      // The POS lives in the waiter section; a manager covering the floor
      // needs it labelled rather than hanging under the previous heading.
      waiter: t('sidebar.sections.customerService')
    },
    chef: {
      main: t('nav.dashboard'),
      chef: t('sidebar.sections.kitchenFocus')
    },
    waiter: {
      main: t('nav.dashboard'),
      waiter: t('sidebar.sections.customerService')
    },
    developer: {
      main: t('nav.dashboard'),
      owner: t('sidebar.sections.developmentTools')
    }
  };
  return headers[role] || {};
};

interface SidebarProps {
  variant?: 'desktop' | 'mobile';
  isOpen?: boolean;
  onClose?: () => void;
}

/**
 * Sidebar Component - Role-based navigation with fixed positioning
 * 
 * Features:
 * - Fixed positioning for desktop to prevent scrolling with content
 * - Role-based navigation items with permission checking
 * - Responsive design with mobile overlay variant
 * - Proper scrolling behavior for navigation content
 * - Dynamic badges and role indicators
 */
export const Sidebar: React.FC<SidebarProps> = ({ 
  variant = 'desktop', 
  isOpen = false, 
  onClose 
}) => {
  const { t } = useSafeTranslation();
  const location = useLocation();
  const { navigate } = useTransitionNavigation();
  const { user, logout } = useAuthStore();
  const { restaurant, getDisplayName } = useRestaurantStore();
  const openingHours = restaurantHelpers.formatOperatingHours(restaurant);

  // Fix malformed permissions for Super Admin
  React.useEffect(() => {
    if (user?.role === 'super_admin' && !user.permissions?.permissions) {
      import('@/stores/authStore').then(({ rolePermissions, useAuthStore }) => {
        const correctPermissions = rolePermissions[user.role];
        if (correctPermissions) {
          const { setUser } = useAuthStore.getState();
          setUser({
            ...user,
            permissions: correctPermissions
          });
        }
      });
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Filter navigation items based on user role and permissions
  const navigationItems = getNavigationItems(t);
  
  const visibleItems = navigationItems.filter(item => {
    if (!user) return false;

    // Check if user has required role
    const hasRole = authHelpers.hasAnyRole(user, item.roles);
    
    // Check if user has required permissions
    const hasPermission = item.permissions.some(permission => 
      authHelpers.hasPermission(user, permission)
    );
    
    // Check if user is restricted from this item
    const isRestricted = item.restrictedRoles?.includes(user.role) || false;
    
    return hasRole && hasPermission && !isRestricted;
  });

  // Group items by section for better organization
  const groupedItems = visibleItems.reduce((acc, item) => {
    const section = item.section || 'main';
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {} as Record<string, NavigationItem[]>);

  const sectionHeaders = getSectionHeaders(user?.role || 'waiter', t);

  // Render navigation section
  const renderNavigationSection = (sectionKey: string, items: NavigationItem[]) => (
    <div key={sectionKey} className="space-y-1">
      {sectionHeaders[sectionKey] && (
        <div className="px-3 pt-1 pb-1.5">
          <h3 className="text-xs font-medium text-neutral-400 uppercase tracking-label">
            {sectionHeaders[sectionKey]}
          </h3>
        </div>
      )}
      {items.map((item) => {
        const isActive = location.pathname === item.href;
        const Icon = item.icon;

        return (
          <Link
            key={item.id}
            to={item.href}
            onClick={variant === 'mobile' ? onClose : undefined}
            className={cn(
              // Active state is a 2px accent rule + weight jump on a transparent
              // ground — never a filled pill. One of the accent's three uses.
              'flex items-center space-x-3 px-3 py-2 text-sm transition-colors duration-150 relative group border-l-2',
              isActive
                ? 'border-accent-600 text-neutral-900 font-bold'
                : 'border-transparent text-neutral-500 font-medium hover:text-neutral-900 hover:border-neutral-300',
              item.highlight && !isActive && 'text-neutral-900'
            )}
            title={item.description}
          >
            <Icon className={cn('h-[18px] w-[18px] flex-shrink-0', isActive ? 'text-neutral-900' : 'text-neutral-400 group-hover:text-neutral-700')} />
            <span className="flex-1">{item.label}</span>
          </Link>
        );
      })}
    </div>
  );

  const sidebarContent = (
    <>
      {/* Nova Cafe Header */}
      <div className="flex h-16 items-center justify-between px-6 border-b border-neutral-200 flex-shrink-0">
        <Link to="/" className="flex items-center flex-1">
          {/* The restaurant's own identity, whenever we have a record for it.
              The logo is optional — most restaurants have not uploaded one —
              so its absence must not fall back to Nova's own wordmark, which
              would silently replace the customer's name with ours. */}
          {user?.role === 'super_admin' || !restaurant ? (
            <LogoFull iconSize={36} />
          ) : (
            <div className="flex items-center gap-3 min-w-0">
              {restaurant.branding?.logo && (
                <img
                  src={restaurant.branding.logo}
                  alt={t('sidebar.brand.logoAlt', { name: restaurant.name })}
                  className="h-9 w-9 rounded-xl object-cover flex-shrink-0"
                />
              )}
              <div className="flex flex-col leading-tight min-w-0">
                <span className="text-base font-bold text-neutral-900 truncate">{getDisplayName()}</span>
                <div className="flex items-center gap-1.5">
                  <LogoIcon size={12} className="text-neutral-900 flex-shrink-0" />
                  <span className="text-xs text-blue-700 font-medium truncate">{t('sidebar.brand.partner', { brand: t('sidebar.brand.name') })}</span>
                </div>
              </div>
            </div>
          )}
        </Link>
        
        {variant === 'mobile' && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <NavClose className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Role indicator & Partnership Status */}
      <div className="px-4 py-3 border-b border-neutral-100 flex-shrink-0 space-y-2">
        <div className="flex items-center justify-between">
          <div className={cn(
            "px-2 py-1 rounded-sm text-xs font-medium",
            authHelpers.getRoleColor(user?.role || 'waiter')
          )}>
            {authHelpers.getRoleDisplayName(user?.role || 'waiter')}
          </div>
{user?.role !== 'super_admin' && <PlanBadge className="text-xs px-2 py-0.5" />}
        </div>
        {/*
          Today's hours, and nothing else. The signed-in name used to lead this
          line, which made it a third copy of the same identity on one screen —
          the sidebar footer below already carries name, role and restaurant.
          Only rendered when the restaurant actually has hours on record.
        */}
        {user?.role !== 'super_admin' && openingHours && (
          <div className="text-xs text-neutral-500">{openingHours}</div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-4 p-4 overflow-y-auto min-h-0">
        {Object.entries(groupedItems).map(([sectionKey, items]) =>
          renderNavigationSection(sectionKey, items)
        )}
      </nav>

      {/*
        User Profile.

        The "Часть сети ресторанов Nova Cafe" panel that used to sit above this
        is gone. It was Nova's own branding, identical on every screen, holding
        permanent space in a workspace whose owner already knows whose platform
        they bought — and it told them nothing they could act on. The sidebar
        header still names the restaurant as a Nova partner, which is where
        that relationship belongs.
      */}
      <div className="border-t border-neutral-200 p-4 flex-shrink-0 space-y-4">
        {/* User Profile */}
        <div className="flex items-center space-x-3">
          <InitialsAvatar
            firstName={user?.name?.split(' ')[0] || ''}
            lastName={user?.name?.split(' ')[1] || ''}
            size="md"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-neutral-900 truncate">
              {authHelpers.getDisplayName(user)}
            </p>
            <p className="text-xs text-neutral-500 truncate">
              {authHelpers.getRoleDisplayName(user?.role || 'waiter')}
              {user?.role !== 'super_admin' ? ` • ${getDisplayName()}` : ` • ${t('superAdmin.platformOwner')}`}
            </p>
          </div>
        </div>
        
        <Button
          variant="ghost"
          fullWidth
          leftIcon={<NavLogout className="h-4 w-4" />}
          onClick={handleLogout}
          className="justify-start text-neutral-600 hover:text-danger-600"
        >
          {t('nav.logout')}
        </Button>
      </div>
    </>
  );

  if (variant === 'mobile') {
    return (
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-neutral-900/50 backdrop-blur-sm lg:hidden"
              onClick={onClose}
            />
            
            <motion.aside
              initial={{ x: -288 }}
              animate={{ x: 0 }}
              exit={{ x: -288 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed left-0 top-0 z-50 h-full w-72 min-w-72 bg-white shadow-xl border-r border-neutral-200 lg:hidden flex flex-col"
              data-testid="sidebar-drawer"
            >
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    );
  }

  return (
    <aside
      data-testid="sidebar-desktop"
      className="hidden lg:flex lg:flex-col w-72 min-w-72 bg-white border-r border-neutral-200 h-screen fixed left-0 top-0 z-30"
    >
      {sidebarContent}
    </aside>
  );
};

export default Sidebar;